import React, { useState, useContext, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { AuthContext } from '../App';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Eye, EyeOff } from 'lucide-react';
import SafetyWaiver from '../components/SafetyWaiver';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = BACKEND_URL;

/**
 * ENTRANCE EXPERIENCE — LOCKED DIRECTIVE
 * 
 * Hierarchy:
 * 1. PRIMARY: "Browse without an account" - Visually dominant, default path
 * 2. SECONDARY: "Create an account" - Visible but not promoted
 * 3. TERTIARY: "Sign in" - De-emphasized, top position for returning users
 * 
 * Philosophy: Trust over conversion. Availability without pressure.
 */

export default function Auth() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, authResolved, setUser } = useContext(AuthContext);
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [showWaiver, setShowWaiver] = useState(false);
  const [referralCode, setReferralCode] = useState(null);
  const [referrerName, setReferrerName] = useState(null);
  
  // View state: 'entrance' | 'signin' | 'signup'
  // Read initial view from URL param: /auth?mode=signin or /auth?mode=signup
  const initialView = searchParams.get('mode') || 'entrance';
  const [currentView, setCurrentView] = useState(
    ['signin', 'signup'].includes(initialView) ? initialView : 'entrance'
  );
  
  const [loginData, setLoginData] = useState({ email: '', password: '' });
  const [magicEmail, setMagicEmail] = useState('');
  const [magicSent, setMagicSent] = useState(false);
  const [magicSending, setMagicSending] = useState(false);
  const [useMagicLink, setUseMagicLink] = useState(false);
  const [inviteCode, setInviteCode] = useState(null);
  const [registerData, setRegisterData] = useState({ 
    email: '', 
    password: '', 
    confirmPassword: '', 
    name: '', 
    username: '', 
    promo_code: ''
  });
  const [usernameAvailable, setUsernameAvailable] = useState(null);
  const [checkingUsername, setCheckingUsername] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetLoading, setResetLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [showLoginPassword, setShowLoginPassword] = useState(false);
  const [registrationError, setRegistrationError] = useState(null);

  // Redirect authenticated users to Base Camp.
  // Uses the already-resolved AuthContext (which comes from the single AppRouter
  // /api/auth/me probe) instead of making a duplicate request from this page.
  useEffect(() => {
    if (authResolved && user) {
      navigate('/', { replace: true });
    }
  }, [authResolved, user, navigate]);

  // Check for referral, promo, or invite code in URL
  useEffect(() => {
    const ref = searchParams.get('ref');
    const promo = searchParams.get('promo');
    const invite = searchParams.get('invite');
    const redirectTo = searchParams.get('redirect');

    if (invite) {
      const code = invite.trim().toUpperCase();
      setInviteCode(code);
      sessionStorage.setItem('lk_pending_invite', code);
      toast.success(`Invite ${code} will be applied when you sign in.`);
    }
    if (redirectTo) {
      sessionStorage.setItem('lk_post_auth_redirect', redirectTo);
    }
    if (promo) {
      setRegisterData(prev => ({ ...prev, promo_code: promo.toUpperCase() }));
    }
    if (ref) {
      setReferralCode(ref);
      // Validate the referral code
      axios.get(`${API}/api/referral/check/${ref}`)
        .then(res => {
          if (res.data.valid) {
            setReferrerName(res.data.referrer_name);
            toast.success(`You were referred by ${res.data.referrer_name}! You'll get 3 extra trial days.`);
          }
        })
        .catch(() => {});
    }
  }, [searchParams]);

  const handleGoogleAuth = () => {
    // Prevent double-click
    if (googleLoading) {
      return;
    }
    
    setGoogleLoading(true);
    
    // Store current location for post-auth redirect
    const currentPath = window.location.pathname + window.location.search;
    const redirectTarget = currentPath === '/auth' ? '/' : currentPath;
    sessionStorage.setItem('lk_post_auth_redirect', redirectTarget);
    
    // Build redirect URL - root origin only (no path). AuthCallbackInner handles
    // the session_id hash globally regardless of route.
    const redirectUrl = `${window.location.origin}`;
    
    // Small delay to ensure state is updated before redirect
    setTimeout(() => {
      window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
    }, 100);
  };

  // Check username availability
  const checkUsername = async () => {
    if (registerData.username.length < 3) {
      setUsernameAvailable(null);
      return;
    }
    setCheckingUsername(true);
    try {
      const response = await axios.get(`${API}/api/username/check/${registerData.username}`);
      setUsernameAvailable(response.data.available);
    } catch {
      setUsernameAvailable(null);
    } finally {
      setCheckingUsername(false);
    }
  };

  const applyPendingInvite = async (token) => {
    const code = sessionStorage.getItem('lk_pending_invite');
    if (!code) return;
    try {
      const r = await axios.post(
        `${API}/api/auth/apply-pending-invite`,
        { code },
        {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
          withCredentials: true,
        }
      );
      if (r.data?.applied) {
        toast.success(`You joined through ${r.data.inviter_name}`);
      }
    } catch { /* silent */ }
    sessionStorage.removeItem('lk_pending_invite');
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(`${API}/api/auth/login`, loginData, {
        withCredentials: true,
      });
      if (response.data?.token) {
        localStorage.setItem('token', response.data.token);
      }
      setUser(response.data.user);
      await applyPendingInvite(response.data?.token);
      toast.success('Login successful!');
      const redirect = sessionStorage.getItem('lk_post_auth_redirect');
      sessionStorage.removeItem('lk_post_auth_redirect');
      navigate(redirect || '/');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  const handleMagicLink = async (e) => {
    e.preventDefault();
    if (!magicEmail) {
      toast.error('Please enter your email');
      return;
    }
    setMagicSending(true);
    try {
      await axios.post(
        `${API}/api/auth/magic-link/request`,
        {
          email: magicEmail,
          invite_code: inviteCode || sessionStorage.getItem('lk_pending_invite') || undefined,
          redirect: sessionStorage.getItem('lk_post_auth_redirect') || undefined,
        },
        { withCredentials: true }
      );
      setMagicSent(true);
      toast.success('Sign-in link sent. Check your email.');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not send link');
    } finally {
      setMagicSending(false);
    }
  };

  const handleRegisterFormSubmit = (e) => {
    e.preventDefault();
    // Check if passwords match
    if (registerData.password !== registerData.confirmPassword) {
      toast.error('Passwords do not match!');
      return;
    }
    if (registerData.password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    // Base state is optional - can be set later in settings
    // Show the safety waiver before completing registration
    setShowWaiver(true);
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    if (!resetEmail) {
      toast.error('Please enter your email address');
      return;
    }
    setResetLoading(true);
    try {
      await axios.post(`${API}/api/auth/forgot-password`, { email: resetEmail });
      toast.success('Password reset link sent to your email!');
      setShowForgotPassword(false);
      setResetEmail('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send reset email');
    } finally {
      setResetLoading(false);
    }
  };

  const handleWaiverAccept = async () => {
    setShowWaiver(false);
    setLoading(true);
    setRegistrationError(null);

    // Debug: log registration data
    console.log('[LikeKind] Registration attempt:', {
      email: registerData.email,
      name: registerData.name,
      username: registerData.username,
      hasPromoCode: !!registerData.promo_code,
      promoCode: registerData.promo_code
    });

    try {
      const payload = {
        email: registerData.email,
        password: registerData.password,
        name: registerData.name,
        username: registerData.username,
        promo_code: registerData.promo_code || null,
        waiver_accepted: true,
        waiver_accepted_at: new Date().toISOString(),
        referral_code: referralCode
      };
      
      console.log('[LikeKind] Sending registration payload to:', `${API}/api/auth/register`);
      
      const response = await axios.post(`${API}/api/auth/register`, payload, {
        withCredentials: true,
      });
      
      console.log('[LikeKind] Registration successful:', response.data.user?.user_id);
      
      if (response.data?.token) {
        localStorage.setItem('token', response.data.token);
      }
      setUser(response.data.user);

      // Apply any pending invite captured pre-registration
      await applyPendingInvite(response.data?.token);
      
      if (referralCode && referrerName) {
        toast.success(`Welcome! You got 3 extra days thanks to ${referrerName}'s referral!`);
      } else {
        toast.success('Registration successful! Welcome to LikeKind!');
      }
      const redirect = sessionStorage.getItem('lk_post_auth_redirect');
      sessionStorage.removeItem('lk_post_auth_redirect');
      navigate(redirect || '/');
    } catch (error) {
      // Enhanced error logging
      console.error('[LikeKind] Registration failed:', {
        status: error.response?.status,
        statusText: error.response?.statusText,
        detail: error.response?.data?.detail,
        message: error.message,
        fullError: error.response?.data
      });
      
      // Extract error message - handle Pydantic validation errors (arrays/objects)
      let errorMessage = 'Registration failed. Please try again.';
      const detail = error.response?.data?.detail;
      
      if (typeof detail === 'string') {
        errorMessage = detail;
      } else if (Array.isArray(detail)) {
        // Pydantic validation errors come as array of objects
        errorMessage = detail.map(e => e.msg || e.message || JSON.stringify(e)).join(', ');
      } else if (detail && typeof detail === 'object') {
        errorMessage = detail.msg || detail.message || JSON.stringify(detail);
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      setRegistrationError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleWaiverDecline = () => {
    setShowWaiver(false);
    toast.info('You must accept the safety guidelines to create an account.');
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: 'var(--fy-background-primary)' }}>
      {/* Safety Waiver Modal */}
      {showWaiver && (
        <SafetyWaiver
          onAccept={handleWaiverAccept}
          onDecline={handleWaiverDecline}
        />
      )}

      {/* ============================================
          ENTRANCE VIEW — Default for first-time users
          ============================================ */}
      {currentView === 'entrance' && (
        <div className="min-h-screen flex flex-col">
          {/* TERTIARY: Sign In - Top right, for returning users */}
          <header className="flex justify-end p-4">
            <button
              onClick={() => setCurrentView('signin')}
              className="text-sm font-medium px-4 py-2 rounded-lg transition-colors"
              style={{ color: 'var(--fy-text-secondary)' }}
              data-testid="signin-link"
            >
              Sign in
            </button>
          </header>

          {/* Main content */}
          <main className="flex-1 flex flex-col items-center justify-center px-6 pb-12">
            {/* Logo — large, grounded */}
            <div className="mb-6">
              <div className="w-24 h-24 sm:w-28 sm:h-28 rounded-3xl shadow-lg overflow-hidden" style={{ backgroundColor: 'var(--fy-background-secondary)' }}>
                <img 
                  src="https://customer-assets.emergentagent.com/job_kindplatform/artifacts/my73uiau_file_00000000f09c720c9463ab506718d330.png" 
                  alt="LikeKind" 
                  className="w-full h-full object-cover" 
                />
              </div>
            </div>

            {/* Title */}
            <h1 
              className="text-4xl sm:text-5xl font-black text-center mb-3"
              style={{ fontFamily: 'Outfit', color: 'var(--fy-text-primary)' }}
            >
              LikeKind
            </h1>
            <p 
              className="text-center mb-3 text-lg font-medium"
              style={{ color: 'var(--fy-text-primary)' }}
            >
              Find and start real-world activities near you.
            </p>
            <p 
              className="text-center mb-10"
              style={{ color: 'var(--fy-text-secondary)' }}
            >
              Join something or start something.
            </p>

            {/* PRIMARY CTAs: Equal weight, side-by-side on desktop, stacked on mobile */}
            <div className="w-full max-w-md flex flex-col sm:flex-row gap-3 mb-6">
              {/* Browse for Free - First position */}
              <Button
                onClick={() => navigate('/base-camp')}
                className="flex-1 h-14 rounded-full font-bold text-base"
                style={{ 
                  backgroundColor: 'var(--lk-primary-green)', 
                  color: '#FFFFFF',
                  boxShadow: '0 4px 14px rgba(122, 143, 91, 0.25)'
                }}
                data-testid="browse-for-free-btn"
              >
                Browse for Free
              </Button>
              
              {/* Create an Account - Second position, equal weight */}
              <Button
                onClick={() => setCurrentView('signup')}
                className="flex-1 h-14 rounded-full font-bold text-base"
                style={{ 
                  backgroundColor: 'var(--lk-earth-brown)', 
                  color: '#FFFFFF',
                  boxShadow: '0 4px 14px rgba(139, 106, 79, 0.25)'
                }}
                data-testid="create-account-btn"
              >
                Create an Account
              </Button>
            </div>

            {/* Referral/Promo/Invite indicator (subtle) */}
            {(referralCode || referrerName || inviteCode) && (
              <p className="text-xs mt-2" style={{ color: 'var(--fy-text-disabled)' }} data-testid="invite-indicator">
                {inviteCode ? `Invite ${inviteCode} will apply after you sign in` : referrerName ? `Referred by ${referrerName}` : 'Promo code applied'}
              </p>
            )}
          </main>
        </div>
      )}

      {/* ============================================
          SIGN IN VIEW — For returning users
          ============================================ */}
      {currentView === 'signin' && (
        <div className="min-h-screen flex flex-col">
          {/* Back button */}
          <header className="flex items-center p-4">
            <button
              onClick={() => setCurrentView('entrance')}
              className="text-sm font-medium px-3 py-2 rounded-lg"
              style={{ color: 'var(--fy-text-secondary)' }}
            >
              ← Back
            </button>
          </header>

          <main className="flex-1 flex flex-col items-center justify-center px-6 pb-12">
            <div className="w-full max-w-sm">
              <h1 
                className="text-2xl font-bold text-center mb-8"
                style={{ fontFamily: 'Outfit', color: 'var(--fy-text-primary)' }}
              >
                Sign in
              </h1>

              {/* Google Sign In */}
              <Button
                data-testid="google-auth-button"
                onClick={handleGoogleAuth}
                disabled={googleLoading}
                variant="outline"
                className="w-full mb-6 h-12 rounded-xl font-medium"
                style={{ border: '1px solid var(--fy-border-subtle)', backgroundColor: 'var(--fy-background-primary)' }}
              >
                {googleLoading ? (
                  <span className="flex items-center">
                    <div className="w-4 h-4 mr-2 border-2 border-gray-300 border-t-gray-600 rounded-full animate-spin"></div>
                    Connecting...
                  </span>
                ) : (
                  <span className="flex items-center justify-center">
                    <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
                      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                      <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                    </svg>
                    Continue with Google
                  </span>
                )}
              </Button>

              {/* Divider */}
              <div className="relative mb-6">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full" style={{ borderTop: '1px solid var(--fy-border-subtle)' }}></div>
                </div>
                <div className="relative flex justify-center text-xs">
                  <span className="px-3" style={{ backgroundColor: 'var(--fy-background-primary)', color: 'var(--fy-text-disabled)' }}>or</span>
                </div>
              </div>

              {/* Email Sign In Form */}
              {!useMagicLink ? (
                <form onSubmit={handleLogin} className="space-y-4">
                <div>
                  <Label htmlFor="login-email" className="text-sm font-medium" style={{ color: 'var(--fy-text-primary)' }}>Email</Label>
                  <Input
                    id="login-email"
                    data-testid="login-email"
                    type="email"
                    required
                    value={loginData.email}
                    onChange={(e) => setLoginData({...loginData, email: e.target.value})}
                    className="mt-1 h-12 rounded-xl"
                    style={{ border: '1px solid var(--fy-border-subtle)', backgroundColor: 'var(--fy-background-primary)' }}
                  />
                </div>

                <div>
                  <Label htmlFor="login-password" className="text-sm font-medium" style={{ color: 'var(--fy-text-primary)' }}>Password</Label>
                  <div className="relative mt-1">
                    <Input
                      id="login-password"
                      data-testid="login-password"
                      type={showLoginPassword ? "text" : "password"}
                      required
                      value={loginData.password}
                      onChange={(e) => setLoginData({...loginData, password: e.target.value})}
                      className="h-12 rounded-xl pr-10"
                      style={{ border: '1px solid var(--fy-border-subtle)', backgroundColor: 'var(--fy-background-primary)' }}
                    />
                    <button
                      type="button"
                      onClick={() => setShowLoginPassword(!showLoginPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2"
                      style={{ color: 'var(--fy-text-disabled)' }}
                    >
                      {showLoginPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                </div>

                <Button
                  data-testid="login-submit"
                  type="submit"
                  disabled={loading}
                  className="w-full h-12 rounded-xl font-medium"
                  style={{ backgroundColor: '#7A8F5B', color: '#FFFFFF' }}
                >
                  {loading ? 'Signing in...' : 'Sign in'}
                </Button>

                <div className="flex items-center justify-between pt-1">
                  <button
                    type="button"
                    onClick={() => { setUseMagicLink(true); setMagicSent(false); setMagicEmail(loginData.email || ''); }}
                    className="text-sm"
                    style={{ color: '#7A8F5B', fontWeight: 500 }}
                    data-testid="use-magic-link-link"
                  >
                    Email me a sign-in link
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowForgotPassword(true)}
                    className="text-sm"
                    style={{ color: 'var(--fy-text-secondary)' }}
                  >
                    Forgot password?
                  </button>
                </div>
              </form>
              ) : (
                <div data-testid="magic-link-form">
                  {!magicSent ? (
                    <form onSubmit={handleMagicLink} className="space-y-4">
                      <div>
                        <Label htmlFor="magic-email" className="text-sm font-medium" style={{ color: 'var(--fy-text-primary)' }}>Email</Label>
                        <Input
                          id="magic-email"
                          data-testid="magic-email-input"
                          type="email"
                          required
                          value={magicEmail}
                          onChange={(e) => setMagicEmail(e.target.value)}
                          placeholder="you@example.com"
                          className="mt-1 h-12 rounded-xl"
                          style={{ border: '1px solid var(--fy-border-subtle)', backgroundColor: 'var(--fy-background-primary)' }}
                        />
                        <p className="text-xs mt-2" style={{ color: 'var(--fy-text-secondary)' }}>
                          We’ll email you a one-time sign-in link. Expires in 15 minutes.
                        </p>
                      </div>
                      <Button
                        type="submit"
                        disabled={magicSending}
                        data-testid="magic-link-submit"
                        className="w-full h-12 rounded-xl font-medium"
                        style={{ backgroundColor: '#7A8F5B', color: '#FFFFFF' }}
                      >
                        {magicSending ? 'Sending…' : 'Send sign-in link'}
                      </Button>
                      <button
                        type="button"
                        onClick={() => setUseMagicLink(false)}
                        className="w-full text-sm pt-1"
                        style={{ color: 'var(--fy-text-secondary)' }}
                      >
                        Use password instead
                      </button>
                    </form>
                  ) : (
                    <div className="text-center space-y-4 py-6" data-testid="magic-link-sent">
                      <h2 className="text-lg font-bold" style={{ color: 'var(--fy-text-primary)' }}>Check your email</h2>
                      <p className="text-sm" style={{ color: 'var(--fy-text-secondary)' }}>
                        If <span className="font-medium" style={{ color: 'var(--fy-text-primary)' }}>{magicEmail}</span> has a LikeKind account,
                        a sign-in link is on its way. It expires in 15 minutes.
                      </p>
                      <button
                        type="button"
                        onClick={() => { setUseMagicLink(false); setMagicSent(false); }}
                        className="text-sm"
                        style={{ color: '#7A8F5B', fontWeight: 500 }}
                      >
                        Back to sign in
                      </button>
                    </div>
                  )}
                </div>
              )}

              {/* Switch to signup */}
              <p className="text-center mt-8 text-sm" style={{ color: 'var(--fy-text-secondary)' }}>
                Need an account?{' '}
                <button
                  onClick={() => setCurrentView('signup')}
                  className="font-medium"
                  style={{ color: '#7A8F5B' }}
                >
                  Create one
                </button>
              </p>
            </div>
          </main>
        </div>
      )}

      {/* ============================================
          SIGN UP VIEW — For users ready to create account
          ============================================ */}
      {currentView === 'signup' && (
        <div className="min-h-screen flex flex-col">
          {/* Back button */}
          <header className="flex items-center p-4">
            <button
              onClick={() => setCurrentView('entrance')}
              className="text-sm font-medium px-3 py-2 rounded-lg"
              style={{ color: 'var(--fy-text-secondary)' }}
            >
              ← Back
            </button>
          </header>

          <main className="flex-1 flex flex-col items-center px-6 pb-12 overflow-y-auto">
            <div className="w-full max-w-sm">
              <h1 
                className="text-2xl font-bold text-center mb-2"
                style={{ fontFamily: 'Outfit', color: 'var(--fy-text-primary)' }}
              >
                Create an account
              </h1>
              <p className="text-center text-sm mb-6" style={{ color: 'var(--fy-text-secondary)' }}>
                Join a calmer community.
              </p>

              {/* Google Sign Up */}
              <Button
                data-testid="google-signup-button"
                onClick={handleGoogleAuth}
                disabled={googleLoading}
                variant="outline"
                className="w-full mb-6 h-12 rounded-xl font-medium"
                style={{ border: '1px solid var(--fy-border-subtle)', backgroundColor: 'var(--fy-background-primary)' }}
              >
                {googleLoading ? (
                  <span className="flex items-center">
                    <div className="w-4 h-4 mr-2 border-2 border-gray-300 border-t-gray-600 rounded-full animate-spin"></div>
                    Connecting...
                  </span>
                ) : (
                  <span className="flex items-center justify-center">
                    <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
                      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                      <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                    </svg>
                    Continue with Google
                  </span>
                )}
              </Button>

              {/* Divider */}
              <div className="relative mb-6">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full" style={{ borderTop: '1px solid var(--fy-border-subtle)' }}></div>
                </div>
                <div className="relative flex justify-center text-xs">
                  <span className="px-3" style={{ backgroundColor: 'var(--fy-background-primary)', color: 'var(--fy-text-disabled)' }}>or</span>
                </div>
              </div>

              {/* Registration Form */}
              <form onSubmit={handleRegisterFormSubmit} className="space-y-4">
                {/* Handle */}
                <div>
                  <Label htmlFor="register-username" className="text-sm font-medium" style={{ color: 'var(--fy-text-primary)' }}>
                    Handle
                  </Label>
                  <div className="relative mt-1">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm" style={{ color: 'var(--fy-text-disabled)' }}>@</span>
                    <Input
                      id="register-username"
                      data-testid="register-username"
                      type="text"
                      required
                      minLength={3}
                      maxLength={20}
                      value={registerData.username}
                      onChange={(e) => {
                        const val = e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '');
                        setRegisterData({...registerData, username: val});
                        setUsernameAvailable(null);
                      }}
                      onBlur={checkUsername}
                      placeholder="yourname"
                      className="h-12 rounded-xl pl-8"
                      style={{ border: '1px solid var(--fy-border-subtle)', backgroundColor: 'var(--fy-background-primary)' }}
                    />
                  </div>
                  {checkingUsername && <p className="text-xs mt-1" style={{ color: 'var(--fy-text-disabled)' }}>Checking...</p>}
                  {usernameAvailable === true && <p className="text-xs mt-1" style={{ color: '#7A8F5B' }}>✓ Available</p>}
                  {usernameAvailable === false && <p className="text-xs mt-1" style={{ color: '#dc2626' }}>✗ Taken</p>}
                </div>

                {/* Name */}
                <div>
                  <Label htmlFor="register-name" className="text-sm font-medium" style={{ color: 'var(--fy-text-primary)' }}>
                    Name <span className="font-normal" style={{ color: 'var(--fy-text-disabled)' }}>(private)</span>
                  </Label>
                  <Input
                    id="register-name"
                    data-testid="register-name"
                    type="text"
                    required
                    value={registerData.name}
                    onChange={(e) => setRegisterData({...registerData, name: e.target.value})}
                    className="mt-1 h-12 rounded-xl"
                    style={{ border: '1px solid var(--fy-border-subtle)', backgroundColor: 'var(--fy-background-primary)' }}
                  />
                </div>

                {/* Email */}
                <div>
                  <Label htmlFor="register-email" className="text-sm font-medium" style={{ color: 'var(--fy-text-primary)' }}>Email</Label>
                  <Input
                    id="register-email"
                    data-testid="register-email"
                    type="email"
                    required
                    value={registerData.email}
                    onChange={(e) => setRegisterData({...registerData, email: e.target.value})}
                    className="mt-1 h-12 rounded-xl"
                    style={{ border: '1px solid var(--fy-border-subtle)', backgroundColor: 'var(--fy-background-primary)' }}
                  />
                </div>

                {/* Password */}
                <div>
                  <Label htmlFor="register-password" className="text-sm font-medium" style={{ color: 'var(--fy-text-primary)' }}>Password</Label>
                  <div className="relative mt-1">
                    <Input
                      id="register-password"
                      data-testid="register-password"
                      type={showPassword ? "text" : "password"}
                      required
                      minLength={6}
                      value={registerData.password}
                      onChange={(e) => setRegisterData({...registerData, password: e.target.value})}
                      className="h-12 rounded-xl pr-10"
                      style={{ border: '1px solid var(--fy-border-subtle)', backgroundColor: 'var(--fy-background-primary)' }}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2"
                      style={{ color: 'var(--fy-text-disabled)' }}
                    >
                      {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                </div>

                {/* Confirm Password */}
                <div>
                  <Label htmlFor="register-confirm-password" className="text-sm font-medium" style={{ color: 'var(--fy-text-primary)' }}>Confirm Password</Label>
                  <div className="relative mt-1">
                    <Input
                      id="register-confirm-password"
                      data-testid="register-confirm-password"
                      type={showConfirmPassword ? "text" : "password"}
                      required
                      value={registerData.confirmPassword}
                      onChange={(e) => setRegisterData({...registerData, confirmPassword: e.target.value})}
                      className="h-12 rounded-xl pr-10"
                      style={{ border: '1px solid var(--fy-border-subtle)', backgroundColor: 'var(--fy-background-primary)' }}
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2"
                      style={{ color: 'var(--fy-text-disabled)' }}
                    >
                      {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                  {registerData.password && registerData.confirmPassword && (
                    registerData.password === registerData.confirmPassword 
                      ? <p className="text-xs mt-1" style={{ color: '#7A8F5B' }}>✓ Passwords match</p>
                      : <p className="text-xs mt-1" style={{ color: '#dc2626' }}>✗ Passwords do not match</p>
                  )}
                </div>

                {/* Promo Code (optional) */}
                <div>
                  <Label htmlFor="register-promo" className="text-sm font-medium" style={{ color: 'var(--fy-text-primary)' }}>
                    Promo code <span className="font-normal" style={{ color: 'var(--fy-text-disabled)' }}>(optional)</span>
                  </Label>
                  <Input
                    id="register-promo"
                    data-testid="register-promo"
                    type="text"
                    value={registerData.promo_code}
                    onChange={(e) => setRegisterData({...registerData, promo_code: e.target.value.toUpperCase()})}
                    placeholder="e.g., LK-FAM-01A9"
                    className="mt-1 h-12 rounded-xl uppercase"
                    style={{ border: '1px solid var(--fy-border-subtle)', backgroundColor: 'var(--fy-background-primary)' }}
                  />
                </div>

                {/* Error Banner */}
                {registrationError && (
                  <div className="p-3 rounded-xl" style={{ backgroundColor: '#fef2f2', border: '1px solid #fecaca' }}>
                    <p className="text-sm" style={{ color: '#dc2626' }}>
                      {typeof registrationError === 'string' ? registrationError : 'Registration failed. Please try again.'}
                    </p>
                  </div>
                )}

                <Button
                  data-testid="register-submit"
                  type="submit"
                  disabled={loading || usernameAvailable === false || registerData.username.length < 3}
                  className="w-full h-12 rounded-xl font-medium"
                  style={{ backgroundColor: '#7A8F5B', color: '#FFFFFF' }}
                >
                  {loading ? 'Creating account...' : 'Create account'}
                </Button>
              </form>

              {/* Switch to signin */}
              <p className="text-center mt-6 text-sm" style={{ color: 'var(--fy-text-secondary)' }}>
                Already have an account?{' '}
                <button
                  onClick={() => setCurrentView('signin')}
                  className="font-medium"
                  style={{ color: '#7A8F5B' }}
                >
                  Sign in
                </button>
              </p>
            </div>
          </main>
        </div>
      )}

      {/* Forgot Password Modal */}
      {showForgotPassword && (
        <div className="fixed inset-0 flex items-center justify-center p-4 z-50" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="w-full max-w-sm rounded-2xl p-6" style={{ backgroundColor: 'var(--fy-background-primary)' }}>
            <h2 className="text-xl font-bold mb-2" style={{ color: 'var(--fy-text-primary)' }}>Reset Password</h2>
            <p className="text-sm mb-4" style={{ color: 'var(--fy-text-secondary)' }}>
              Enter your email and we will send you a reset link.
            </p>
            <form onSubmit={handleForgotPassword} className="space-y-4">
              <Input
                type="email"
                required
                value={resetEmail}
                onChange={(e) => setResetEmail(e.target.value)}
                placeholder="Your email"
                className="h-12 rounded-xl"
                style={{ border: '1px solid var(--fy-border-subtle)' }}
              />
              <div className="flex gap-3">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setShowForgotPassword(false);
                    setResetEmail('');
                  }}
                  className="flex-1 h-12 rounded-xl"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={resetLoading}
                  className="flex-1 h-12 rounded-xl"
                  style={{ backgroundColor: '#7A8F5B', color: '#FFFFFF' }}
                >
                  {resetLoading ? 'Sending...' : 'Send Link'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
